import React from "react";
import { Star } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

/**
 * Google Reviews Badge - matches reference design:
 * Google G logo + 5 yellow stars + "GOOGLE / Rezensionen" text
 */
const GoogleReviewsBadge = ({ url = "https://www.google.com/search?q=Malergesch%C3%A4ft+Sammy+Felber+Basel", className = "", variant = "light" }) => {
  const { lang } = useT();

  const label = {
    de: { top: "GOOGLE", bottom: "8 Rezensionen" },
    en: { top: "GOOGLE", bottom: "8 Reviews" },
    fr: { top: "GOOGLE", bottom: "8 Avis" },
    it: { top: "GOOGLE", bottom: "8 Recensioni" },
  }[lang];

  const isDark = variant === "dark";

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className={`inline-flex items-center gap-4 px-5 py-3.5 border transition-all hover:shadow-xl hover:-translate-y-0.5 ${
        isDark ? "bg-zinc-900/90 border-zinc-700 text-white" : "bg-white border-zinc-200 text-zinc-900"
      } ${className}`}
      data-testid="google-reviews-badge"
    >
      {/* Google G logo */}
      <svg className="w-8 h-8 flex-shrink-0" viewBox="0 0 48 48">
        <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
        <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
        <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
        <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
      </svg>

      {/* Stars + rating */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <Star key={s} size={16} className="fill-yellow-400 text-yellow-400" />
          ))}
        </div>
        <span className={`text-sm font-bold ${isDark ? "text-white" : "text-zinc-900"}`}>5.0</span>
      </div>

      {/* Label */}
      <div className="leading-tight border-l pl-4 border-zinc-200">
        <div className={`text-[10px] tracking-[0.2em] font-semibold ${isDark ? "text-zinc-400" : "text-zinc-500"}`}>{label.top}</div>
        <div className={`text-sm font-bold ${isDark ? "text-white" : "text-zinc-900"}`}>{label.bottom}</div>
      </div>
    </a>
  );
};

export default GoogleReviewsBadge;
